/**
 * SoleadLab 2.0 - Tasks & Issues Types
 *
 * Data model for the issue-to-task workflow system.
 * Issues are created from thermal indications or monitoring alarms.
 * Tasks are created from issues and assigned for maintenance resolution.
 */

import type { ThermalAnalysisData } from '../components/editor/thermal/thermalTypes';

// ============================================
// Enums / Unions
// ============================================

export type IssueSeverity = 'Critical' | 'Major' | 'Minor' | 'Info';
export type IssueState = 'Open' | 'Acknowledged' | 'ConvertedToTask' | 'Closed' | 'Suppressed';
export type IssueSourceType = 'thermal_indication' | 'monitoring_alert';

export type TaskPriority = 'P1' | 'P2' | 'P3' | 'P4';
export type TaskStatus = 'Draft' | 'Scheduled' | 'Assigned' | 'InProgress' | 'Done' | 'Canceled';

export type TasksTabId = 'tasks' | 'completed' | 'audit';
export type IssueFilterId = 'all' | 'open' | 'critical' | 'major';

// ============================================
// Audit
// ============================================

export interface AuditEntry {
  timestamp: string;
  action: string;
  user: string;
  details: string;
}

// ============================================
// Issue
// ============================================

export interface AssetPath {
  station?: string;
  inverter?: string;
  mppt?: string;
  dccb?: string;
  string?: string;
  module?: string;
}

export interface EstimatedImpact {
  powerLossW: number;
  energyLossKWh: number;
  revenueLoss: number;
}

export interface IssueSuppression {
  reason: string;
  until: string;
  notes: string;
  timestamp: string;
}

/** Alarm detail data stored on issues synced from advanced alarm rules */
export interface AdvancedAlarmData {
  ruleType: 'zero_check' | 'deviation' | 'expression';
  ruleName: string;
  details: string;
  matchedMetrics?: Array<{ entity_id: string; metric: string; value: number | string | null }>;
}

export interface Issue {
  issueId: string;
  sourceType: IssueSourceType;
  sourceRefId: string;
  siteId: string;
  assetPath: AssetPath;
  geo: { lng: number; lat: number };
  category: string;
  severity: IssueSeverity;
  indType: string;
  confidence: number;
  detectedAt: string;
  lastSeenAt: string;
  state: IssueState;
  image?: string;
  /** PFC-4B: for a child fault with no image of its own, the source Photo's id —
   *  used to resolve the source image for display (the issue never copies it). */
  sourcePhotoId?: string;
  thermalSnapshot?: string;
  thermalData?: ThermalAnalysisData;
  recommendedAction: string;
  estimatedImpact: EstimatedImpact;
  notes: string;
  suppression?: IssueSuppression;
  advancedAlarmData?: AdvancedAlarmData;
  auditLog: AuditEntry[];
  /** V4 entity version for optimistic concurrency */
  _version?: number;
}

// ============================================
// Task
// ============================================

export interface TaskAssignee {
  userId: string;
  role: string;
}

export interface TaskSchedule {
  start?: string;
  end?: string;
  timezone?: string;
  allDay?: boolean;
}

export interface TaskSLA {
  responseBy?: string;
  resolveBy?: string;
}

export interface WorkReport {
  completedBy: string;
  completedAt: string;
}

export interface Task {
  taskId: string;
  title: string;
  description: string;
  linkedIssueIds: string[];
  priority: TaskPriority;
  status: TaskStatus;
  assignees: TaskAssignee[];
  sla: TaskSLA;
  schedule: TaskSchedule;
  estimatedImpact: EstimatedImpact;
  checklist: string[];
  workReport?: WorkReport;
  auditLog: AuditEntry[];
  /** V4 entity version for optimistic concurrency */
  _version?: number;
}

// ============================================
// Persistence wrapper
// ============================================

export interface TasksConfig {
  issues: Issue[];
  tasks: Task[];
  auditLog: AuditEntry[];
}

// ============================================
// Constants / Mappings
// ============================================

/** Map indication type → issue severity */
export const INDICATION_SEVERITY: Record<string, IssueSeverity> = {
  'String OC': 'Critical',
  'O.circuit': 'Critical',
  'Diode': 'Major',
  'Hot spots': 'Major',
  'PID': 'Major',
  'Crack': 'Minor',
  'Glass': 'Minor',
  'Opt.OC': 'Minor',
  'Opt.SC': 'Minor',
  'Riso/Pol': 'Minor',
  'Soiling': 'Info',
  'EL': 'Info',
  'Tracker': 'Major',
  'Vegetation': 'Minor',
  'IV Curve': 'Info',
  'IR Comp.': 'Info',
};

/** Map severity → task priority */
export const SEVERITY_PRIORITY: Record<IssueSeverity, TaskPriority> = {
  Critical: 'P1',
  Major: 'P2',
  Minor: 'P3',
  Info: 'P4',
};

/** Map indication type → issue category */
export const TYPE_CATEGORY: Record<string, string> = {
  'Hot spots': 'Hotspot',
  'Diode': 'Hotspot',
  'String OC': 'String outage',
  'O.circuit': 'String outage',
  'Crack': 'Cell damage',
  'Glass': 'Cell damage',
  'PID': 'Degradation',
  'Opt.OC': 'String outage',
  'Opt.SC': 'String outage',
  'Riso/Pol': 'Degradation',
  'Soiling': 'Maintenance',
  'EL': 'Inspection',
  'Tracker': 'Mechanical',
  'Vegetation': 'Maintenance',
  'IV Curve': 'Inspection',
  'IR Comp.': 'Inspection',
};

/** Recommended action text by indication type */
export const RECOMMENDED_ACTIONS: Record<string, string> = {
  'Hot spots': 'Inspect module for hotspot source. Check bypass diode and connections.',
  'Diode': 'Replace bypass diode. Check for cell damage causing bypass activation.',
  'String OC': 'Check string connections, combiner box fuses, and DC cabling.',
  'O.circuit': 'Inspect for open circuit. Check connectors and junction boxes.',
  'Crack': 'Replace module if crack compromises performance or safety.',
  'Glass': 'Replace module. Glass damage risks moisture ingress.',
  'PID': 'Apply PID recovery or replace affected modules.',
  'Opt.OC': 'Check optical measurement. Verify string connections.',
  'Opt.SC': 'Check for short circuit condition. Inspect wiring.',
  'Riso/Pol': 'Test insulation resistance. Check for moisture ingress.',
  'Soiling': 'Schedule panel cleaning for affected area.',
  'Tracker': 'Inspect tracker drive, motor, and controller; verify tracking movement and alignment.',
  'Vegetation': 'Schedule vegetation clearing around the affected modules to remove shading.',
};

/** Severity badge colors */
export const SEVERITY_COLORS: Record<IssueSeverity, string> = {
  Critical: '#dc2626',
  Major: '#ea580c',
  Minor: '#ca8a04',
  Info: '#0284c7',
};

/** Priority badge colors */
export const PRIORITY_COLORS: Record<TaskPriority, string> = {
  P1: '#dc2626',
  P2: '#ea580c',
  P3: '#ca8a04',
  P4: '#0284c7',
};

/** Task status badge colors */
export const STATUS_COLORS: Record<TaskStatus, string> = {
  Draft: '#64748b',
  Scheduled: '#2563eb',
  Assigned: '#4f46e5',
  InProgress: '#ca8a04',
  Done: '#16a34a',
  Canceled: '#94a3b8',
};

/** Issue state badge colors */
export const STATE_COLORS: Record<IssueState, string> = {
  Open: '#2563eb',
  Acknowledged: '#ca8a04',
  ConvertedToTask: '#16a34a',
  Closed: '#64748b',
  Suppressed: '#94a3b8',
};
